import { Typography } from '@mui/material';
import cx from 'classnames';
import { useFormikContext } from 'formik';
import { map, find } from 'lodash';
import { Fragment, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { payoutRequisitesAutomationApi } from 'api';
import { DataWrapper, FormikSelect } from 'components';
import { QueryKey } from 'enums';
import { PayoutAutomationAccountInfoDetails } from 'features/payout-requisites';
import { useMutation, usePayoutAutomationUtils, usePrevious } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { PayoutOrder } from 'types';

type Props = {
  order: PayoutOrder;
};

export const ConfirmPaymentAutomation: React.FC<Props> = ({ order }) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.confirm_payment_dialog',
  });

  const formik = useFormikContext<{ automationId: string }>();
  const prevAutomationId = usePrevious(formik.values.automationId);
  const { getAutomationOptions } = usePayoutAutomationUtils();

  const queryResultAutomations = useQuery(QueryKey.PayoutOrderAutomations, () =>
    payoutRequisitesAutomationApi.getOrderAutomations(order.id),
  );

  const accountInfoMutation = useMutation(
    payoutRequisitesAutomationApi.getAccountInfo,
    {
      notifierType: 'execute',
    },
  );

  const selectedAutomation = useMemo(
    () =>
      find(
        queryResultAutomations.data,
        (automation) => automation.id === formik.values.automationId,
      ),
    [formik.values.automationId, queryResultAutomations.data],
  );

  const accountInfoAutomation = useMemo(
    () => accountInfoMutation.data! || selectedAutomation,
    [accountInfoMutation.data, selectedAutomation],
  );

  const automationOptions = useMemo(
    () =>
      getAutomationOptions(
        map(queryResultAutomations.data, (automation) =>
          accountInfoMutation.data?.id === automation.id
            ? accountInfoAutomation
            : automation,
        ),
      ),
    [
      getAutomationOptions,
      queryResultAutomations.data,
      accountInfoMutation.data?.id,
      accountInfoAutomation,
    ],
  );

  useEffect(() => {
    if (
      accountInfoMutation.data &&
      prevAutomationId !== formik.values.automationId
    ) {
      accountInfoMutation.reset();
    }
  }, [accountInfoMutation, prevAutomationId, formik.values.automationId]);

  return (
    <div>
      <DataWrapper queryResult={queryResultAutomations}>
        <Fragment>
          <FormikSelect
            name="automationId"
            label={t('automation')}
            options={automationOptions}
            fullWidth
          />
          {formik.values.automationId && (
            <div
              className={cx('tw-mt-2', {
                'tw-flex tw-items-center': !accountInfoAutomation,
              })}
            >
              <Typography sx={{ mr: 1 }} variant="subtitle1">{`${t(
                'account_info',
              )}:`}</Typography>
              <PayoutAutomationAccountInfoDetails
                automation={accountInfoAutomation}
                disabled={accountInfoMutation.isLoading}
                getAccountInfo={() => {
                  accountInfoMutation.mutate(formik.values.automationId);
                }}
              />
            </div>
          )}
        </Fragment>
      </DataWrapper>
    </div>
  );
};
